import type { TFunction } from "i18next"

import { isRichTextEmpty } from "../../../components/RichTextEditor/rich-text-html.ts"
import type { AutomationOptions } from "../types/automation.types"
import { CUSTOM } from "./duration-presets"
import {
  type AutomationFormValues,
  cadenceTotalHours,
  delayTotalMinutes
} from "./form-values"

export type BuilderSectionKey = "basics" | "trigger" | "message" | "sender"

export const BUILDER_SECTIONS: { key: BuilderSectionKey; number: number }[] = [
  { key: "basics", number: 1 },
  { key: "trigger", number: 2 },
  { key: "message", number: 3 },
  { key: "sender", number: 4 }
]

export const sectionTitle = (t: TFunction, key: BuilderSectionKey) =>
  t(`settingsAutomation.builder.sections.${key}`)

export const sectionElementId = (key: BuilderSectionKey) =>
  `automation-section-${key}`
export const sectionHeadingId = (key: BuilderSectionKey) =>
  `automation-section-${key}-heading`

const isValidTotal = (count: number | null | undefined, max: number) =>
  typeof count === "number" && count >= 2 && count <= max

export interface MissingChoice {
  section: BuilderSectionKey
  /** The field that takes focus when the choice is picked from the list. */
  field: keyof AutomationFormValues
  /** An i18next key; the list translates it where it is shown. */
  labelKey: string
}

// What saving still needs, in on-screen order, from the same values the form
// submits. Mirrors the form's own required-field rules.
export const missingChoices = (
  values: AutomationFormValues,
  options: AutomationOptions
): MissingChoice[] => {
  const missing: MissingChoice[] = []
  const add = (
    section: BuilderSectionKey,
    field: keyof AutomationFormValues,
    choice: string
  ) =>
    missing.push({
      section,
      field,
      labelKey: `settingsAutomation.review.missing.${choice}`
    })

  if (!values.name?.trim()) add("basics", "name", "name")
  const channelAvailable = options.channels.some(
    (channel) =>
      channel.key === values.channel && channel.status === "available"
  )
  if (!channelAvailable) add("basics", "channel", "channel")
  // A Nurturing automation has no trigger state to choose.
  if (values.category !== "NURTURING" && values.lead_status == null) {
    add("trigger", "lead_status", "leadStatus")
  }
  if (delayTotalMinutes(values) < 1) {
    add(
      "trigger",
      values.delay_preset === CUSTOM ? "delay_weeks" : "delay_preset",
      "delay"
    )
  }
  if (values.repeat) {
    if (!isValidTotal(values.send_count, options.limits.max_sends)) {
      add("trigger", "send_count", "sendCount")
    }
    if (cadenceTotalHours(values) < 1) {
      add(
        "trigger",
        values.cadence_preset === CUSTOM ? "cadence_weeks" : "cadence_preset",
        "cadence"
      )
    }
  }
  if (!values.subject?.trim()) add("message", "subject", "subject")
  if (isRichTextEmpty(values.body)) add("message", "body", "body")
  if (!values.sender_user) add("sender", "sender_user", "sender")
  return missing
}

// Whether each section holds everything saving needs.
export const sectionCompletion = (
  values: AutomationFormValues,
  options: AutomationOptions
): Record<BuilderSectionKey, boolean> => {
  const incomplete = new Set(
    missingChoices(values, options).map((item) => item.section)
  )
  return {
    basics: !incomplete.has("basics"),
    trigger: !incomplete.has("trigger"),
    message: !incomplete.has("message"),
    sender: !incomplete.has("sender")
  }
}
